import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Badge,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@tonic/shared/components/ui';
import { Plus, Settings, Edit, Recycle, Leaf } from 'lucide-react';

// Mock data for recycling programs
const recyclingPrograms = [
  {
    id: 'RCP001',
    name: 'Furniture Take-Back',
    description: 'Sustainable recycling program for used furniture',
    status: 'active',
    eligibility: [
      'Clean and disassembled',
      'No major damage',
      'Original packaging preferred but not required'
    ],
    benefits: [
      'Store credit for recycled items',
      'Free pickup service',
      'Sustainable disposal',
      'Contribution to circular economy'
    ],
    rewards: {
      basic: {
        credit: '10% of original price',
        conditions: 'Good condition, with receipt'
      },
      standard: {
        credit: '15% of original price',
        conditions: 'Excellent condition, with receipt'
      },
      premium: {
        credit: '20% of original price',
        conditions: 'Like new condition, with original packaging'
      }
    },
    metrics: {
      totalRecycled: 1245,
      materialsSaved: '45 tons',
      customerParticipation: 94,
      environmentalImpact: '32 tons CO2 saved'
    }
  },
  {
    id: 'RCP002',
    name: 'Packaging Recycling Initiative',
    description: 'Comprehensive recycling program for all packaging materials',
    status: 'active',
    eligibility: [
      'Any packaging',
      'Sorted by material type',
      'Clean and dry',
      'Flattened when possible'
    ],
    benefits: [
      'Reward points',
      'Environmental impact tracking',
      'Monthly eco-rewards',
      'Community recycling badges'
    ],
    rewards: {
      basic: {
        credit: '50 points',
        conditions: 'Small packaging items'
      },
      standard: {
        credit: '100 points',
        conditions: 'Medium packaging items'
      },
      premium: {
        credit: '200 points',
        conditions: 'Large packaging items'
      }
    },
    metrics: {
      totalRecycled: 25600,
      materialsSaved: '85 tons',
      customerParticipation: 88,
      environmentalImpact: '120 tons CO2 saved'
    }
  }
];

export function RecycleProgramPage() {
  const [activeTab, setActiveTab] = React.useState('overview');

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Recycling Programs</h1>
          <p className="text-sm text-muted-foreground">
            Manage and configure recycling programs
          </p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          New Program
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {recyclingPrograms.map((program) => (
          <Card key={program.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Leaf className="w-5 h-5" />
                    {program.name}
                  </CardTitle>
                  <CardDescription>{program.description}</CardDescription>
                </div>
                <Badge variant={program.status === 'active' ? 'default' : 'secondary'}>
                  {program.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-medium mb-2">Reward Tiers</h3>
                <div className="space-y-2">
                  {Object.entries(program.rewards).map(([tier, details]) => (
                    <div key={tier} className="flex justify-between">
                      <span className="capitalize">{tier}</span>
                      <span className="font-medium">{details.credit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-2">Performance</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Recycled</p>
                    <p className="text-lg font-medium">{program.metrics.totalRecycled}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Materials Saved</p>
                    <p className="text-lg font-medium">{program.metrics.materialsSaved}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Participation</p>
                    <p className="text-lg font-medium">{program.metrics.customerParticipation}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Environmental Impact</p>
                    <p className="text-lg font-medium">{program.metrics.environmentalImpact}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-2">
                <Button variant="outline" size="sm">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button variant="outline" size="sm">
                  <Settings className="w-4 h-4 mr-2" />
                  Configure
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Program Details</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="eligibility">Eligibility</TabsTrigger>
              <TabsTrigger value="benefits">Benefits</TabsTrigger>
              <TabsTrigger value="rewards">Rewards</TabsTrigger>
              <TabsTrigger value="metrics">Metrics</TabsTrigger>
            </TabsList>

            {recyclingPrograms.map((program) => (
              <React.Fragment key={program.id}>
                <TabsContent value="eligibility" className="mt-6">
                  <div className="space-y-4">
                    <h3 className="font-medium">Eligibility Criteria</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      {program.eligibility.map((criterion, index) => (
                        <li key={index}>{criterion}</li>
                      ))}
                    </ul>
                  </div>
                </TabsContent>

                <TabsContent value="benefits" className="mt-6">
                  <div className="space-y-4">
                    <h3 className="font-medium">Program Benefits</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      {program.benefits.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                </TabsContent>
              </React.Fragment>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
